import { viewerStrings } from '../copy/strings.js';
import { message, CopyError } from '../copy/text.js';
import type {
  RelationshipCounts,
  RelationshipField,
  RelationshipQueryDefinition,
} from '@cdr-clarity/contracts';
import { unifiedRecordPage, type ExecuteRelationshipQuery } from '../state/unified-record-page.js';
import { CsvBuffer } from './csv-buffer.js';
import { cellText, fieldTitle } from './cells.js';

export interface RelationshipExportOptions {
  query: RelationshipQueryDefinition;
  fields: readonly RelationshipField[];
  mode: 'raw' | 'decoded';
  timezone: string;
  cancelled(): boolean;
  current(): boolean;
  progress(rows: number, total: number): void;
  maxBytes?: number;
}

/** Export scalar associations without collapsing observations or labelling them additional calls. */
export async function exportRelationships(
  execute: ExecuteRelationshipQuery,
  options: RelationshipExportOptions,
) {
  const buffer = new CsvBuffer(options.maxBytes ?? 256 * 1024 * 1024);
  let cursors: readonly string[] | null = null;
  let counts: RelationshipCounts | undefined;
  let rows = 0;
  const check = () => {
    if (options.cancelled())
      throw new CopyError(
        viewerStrings.webPresentationExportRelationships.exportCancelledNoFileWasDownloaded,
      );
    if (!options.current())
      throw new CopyError(
        viewerStrings.webPresentationExportRelationships.theRecordsOrViewChangedRestartThe,
      );
  };
  check();
  buffer.append(
    options.fields.map((field) =>
      options.mode === 'raw' ? field.id : fieldTitle(field, options.timezone),
    ),
  );
  do {
    check();
    const page = await unifiedRecordPage(
      execute,
      { ...options.query, page: { size: 500 } },
      options.fields.map((field) => field.id),
      cursors,
    );
    check();
    if (counts && JSON.stringify(counts) !== JSON.stringify(page.result.counts))
      throw new CopyError(
        viewerStrings.webPresentationExportRelationships
          .theAssociationCountsChangedBetweenExportPages,
      );
    counts = page.result.counts;
    for (const row of page.result.rows) {
      buffer.append(
        options.fields.map((field) =>
          options.mode === 'raw'
            ? (row.cells[field.id]?.raw ?? null)
            : cellText(row.cells[field.id], field, options.timezone),
        ),
      );
      rows++;
    }
    options.progress(rows, counts.memberships);
    cursors = page.nextCursors;
  } while (cursors);
  if (rows !== counts.memberships)
    throw new CopyError(
      message(
        viewerStrings.webPresentationExportRelationships.exportReturnedOfAssociationRowsNoFile,
        { value1: rows, value2: counts.memberships },
      ),
    );
  check();
  return { ...buffer.finish(), rows, counts };
}
